import "../../css/GlobalStyles.css";
import { Colors } from "../../utils/colors";
import {
  NotificationType,
  PlanTypeV4,
  subscriptionPricesV4,
  testSubscriptionPricesV4,
} from "@markit/common.types";
import {
  hasSubscription,
  MARKIT_HOTLINE_NUMBER,
  mostRecentSubscription,
} from "@markit/common.utils";
import { useSelector, useDispatch } from "react-redux";
import {
  getAccountState,
  getIncompleteOnboardingFlows,
  LoginState,
} from "../../redux/slices/accountSlice";
import { formatPhoneNumber } from "../../utils/FormatPhoneNumber";
import SubscriptionPlans from "./SubscriptionPlans";
import { FlatMobileTabs } from "../Tabs/FlatMobileTabs";
import SubscriptionReceipt from "./SubscriptionReceipt";
import SubscriptionCheckout from "./SubscriptionCheckout";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { MixpanelContext } from "../../context/AnalyticsService";
import MarkitLogoBlue from "../../assets/MarkitLogoBlue.png";
import ConfirmActionModal from "../Containers/ConfirmPopups/ConfirmActionModal";
import { NavigationId } from "../../navigation/AppParamList";
import { API } from "../../API";
import {
  onCreatorPanelNavigatePath,
  scrollToTop,
} from "../../utils/navigationUtils";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { useWindowResize } from "../../hooks/useWindowResize";
import { checkIsDesktop } from "../../utils/screenResponsivenessUtils";
import { useSlideUpAnimation } from "../../hooks/animationHooks/useSlideUpAnimation";
import { testStripe } from "../../utils/stripeAccountUtils";
import { useOnMount } from "../../hooks/useOnMount";
import AlertContainer from "../Containers/AlertContainer";
import { ONBOARDING_FLOW_FEATURES } from "./SubscriptionPlanCard";

export enum SubscriptionStage {
  OVERVIEW = "Overview",
  CHECKOUT = "Checkout",
  RECEIPT = "Receipt",
}

type SubscriptionPlansFlowProps = {
  onboardingFreeOnPress?: () => void;
};

const SubscriptionPlansFlow = (props: SubscriptionPlansFlowProps) => {
  const { onboardingFreeOnPress } = props;
  const { state, pathname } = useLocation() || {};
  const [searchParams] = useSearchParams();
  const { changePlan, targetPlan: targetPlanState } = state || {};
  const targetPlan =
    targetPlanState ?? searchParams.get("targetPlan") ?? undefined; // targetPlanState - webapp, targetPlan - in-app
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { appInitialized, loggedIn, accountData } =
    useSelector(getAccountState).account;
  const incompleteOnboardingFlows = useSelector(getIncompleteOnboardingFlows);
  const mixpanel = useContext(MixpanelContext);
  const [subscriptionStage, setSubscriptionStage] = useState<SubscriptionStage>(
    SubscriptionStage.OVERVIEW
  );
  const [email, setEmail] = useState<string>(accountData.email);
  // Sales tax (cents) from the created subscription's first invoice; shared so
  // the receipt can show the same tax-inclusive total as checkout.
  const [taxAmount, setTaxAmount] = useState(0);
  const [selectedCardOption, setSelectedCardOption] = useState<number>(2);
  const [loading, setLoading] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Set once an upgrade request has been submitted
  const [pendingUpgrade, setPendingUpgrade] = useState<
    { targetBaselineId: string; hostedInvoiceUrl?: string } | undefined
  >(undefined);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const upgradeCompleteResolver = useRef<(() => void) | null>(null);
  const [alertText, setAlertText] = useState("");

  const windowWidth = useWindowResize();

  useSlideUpAnimation();

  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth, true),
    [windowWidth]
  );

  const isOnboarding = useMemo(
    () => pathname === NavigationId.ONBOARDING,
    [pathname]
  );

  const hasOnboardingFlowFeatures = useMemo(
    () =>
      isOnboarding &&
      incompleteOnboardingFlows.some((flow) =>
        ONBOARDING_FLOW_FEATURES.includes(flow.id)
      ),
    [isOnboarding, incompleteOnboardingFlows]
  );

  useEffect(() => {
    scrollToTop();
  }, [subscriptionStage]);

  useOnMount(() => {
    // Enter if targetPlan is defined, so we can set the selected card option based on the target plan
    if (targetPlan) {
      const planIndex =
        targetPlan === PlanTypeV4.ESSENTIAL.name
          ? 1
          : targetPlan === PlanTypeV4.PRO.name
            ? 2
            : targetPlan === PlanTypeV4.BUSINESS.name
              ? 3
              : 2;
      setSelectedCardOption(planIndex);
    }
  });

  useEffect(() => {
    if (!appInitialized) {
      return;
    }
    if (loggedIn !== LoginState.LOGGED_IN) {
      navigate(NavigationId.WELCOME);
    }
    // Enter if targetPlan is not defined and loading is true
    if (loading && !targetPlan) {
      const subscription = mostRecentSubscription(accountData);
      if (hasSubscription(accountData) && subscription) {
        const mostRecentPrice = subscription.price[0];
        if (
          mostRecentPrice.id === PlanTypeV4.ESSENTIAL.priceId ||
          mostRecentPrice.id === PlanTypeV4.ESSENTIAL.testPriceId
        ) {
          setSelectedCardOption(1);
        } else if (
          mostRecentPrice.id === PlanTypeV4.PRO.priceId ||
          mostRecentPrice.id === PlanTypeV4.PRO.testPriceId
        ) {
          setSelectedCardOption(2);
        } else {
          setSelectedCardOption(3);
        }
      }
    }
    setLoading(false);
  }, [accountData, appInitialized, loading, loggedIn, navigate, targetPlan]);

  const upgradeSubscription = useCallback(async () => {
    try {
      setUpgradeLoading(true);
      let hostedInvoiceUrl: string | undefined;
      let targetBaselineId: string;
      if (testStripe(accountData.uid)) {
        const { id, overflow } =
          testSubscriptionPricesV4[selectedCardOption - 1];
        targetBaselineId = id;
        const res = await API.user.updateTestSubscription(accountData.uid, {
          action: "upgrade",
          baseline: id,
          overflow: overflow,
        });
        hostedInvoiceUrl = res.hostedInvoiceUrl;
      } else {
        const { id, overflow } = subscriptionPricesV4[selectedCardOption - 1];
        targetBaselineId = id;
        const res = await API.user.updateSubscription(accountData.uid, {
          action: "upgrade",
          baseline: id,
          overflow: overflow,
        });
        hostedInvoiceUrl = res.hostedInvoiceUrl;
      }

      setPendingUpgrade({ targetBaselineId, hostedInvoiceUrl });
      if (hostedInvoiceUrl) {
        // Card couldn't be auto charged, so we need to show the invoice modal
        setShowInvoiceModal(true);
        return;
      }

      // Auto-charge succeeded
      await new Promise<void>((resolve) => {
        upgradeCompleteResolver.current = resolve;
      });
    } catch (err: any) {
      setAlertText(
        "There was an error changing your subscription. Please try again later"
      );
      console.log(err.message);
    } finally {
      setUpgradeLoading(false);
    }
  }, [accountData.uid, selectedCardOption]);

  // Confirms the upgrade actually applied
  useEffect(() => {
    if (!pendingUpgrade) {
      return;
    }
    const subscription = mostRecentSubscription(accountData);
    const currentBaselineId = subscription?.price.find(
      (price) => price.type === "baseline"
    )?.id;
    if (currentBaselineId === pendingUpgrade.targetBaselineId) {
      setPendingUpgrade(undefined);
      setShowInvoiceModal(false);
      upgradeCompleteResolver.current?.();
      upgradeCompleteResolver.current = null;
      showNotificationBanner(
        dispatch,
        "Upgraded Subscription!",
        NotificationType.AFFIRMATIVE
      );
      onCreatorPanelNavigatePath(navigate, NavigationId.HOME_SETTINGS, 1);
    }
  }, [accountData, dispatch, navigate, pendingUpgrade]);

  // Only the auto-charge case needs a timeout - the invoice-pay case is
  // inherently open-ended (the user might pay it minutes or hours later), and
  // it's shown as a dismissible modal rather than a blocking spinner anyway.
  // Lets go of the confirm modal's spinner instead of blocking it forever,
  // but keeps watching in the background - the success banner still fires
  // whenever it does land.
  useEffect(() => {
    if (!pendingUpgrade || pendingUpgrade.hostedInvoiceUrl) {
      return;
    }
    const timeout = setTimeout(() => {
      upgradeCompleteResolver.current?.();
      upgradeCompleteResolver.current = null;
      setAlertText(
        "Your payment went through - still finishing your upgrade. Check back in a moment."
      );
    }, 20000);
    return () => clearTimeout(timeout);
  }, [pendingUpgrade, dispatch]);

  const subscriptionCard = useMemo(
    () =>
      selectedCardOption === 1
        ? PlanTypeV4.ESSENTIAL
        : selectedCardOption === 2
          ? PlanTypeV4.PRO
          : selectedCardOption === 3
            ? PlanTypeV4.BUSINESS
            : PlanTypeV4.FREE,
    [selectedCardOption]
  );

  const mostRecentPriceAmount = useMemo(() => {
    const subscription = mostRecentSubscription(accountData);
    if (subscription) {
      return subscription.price[0].amount;
    }
    return 0;
  }, [accountData]);

  /**
   * @TODO - Subscripton updating probably should not be here.
   */
  const subscribe = useCallback(
    async (cardOption: number) => {
      if (loading) {
        return;
      }
      if (hasSubscription(accountData) && !changePlan) {
        alert("You already have a subscription!");
        return;
      }
      const action = changePlan
        ? mostRecentPriceAmount > subscriptionCard.price
          ? "downgrade"
          : "upgrade"
        : "";
      // TODO (Peter): Do not allow manual downgrades, until behavior is verified
      if (action === "downgrade") {
        alert(
          `To downgrade your subscription plan, please contact us at ${formatPhoneNumber(
            MARKIT_HOTLINE_NUMBER
          )}. Thank you.`
        );
        return;
      }
      // if upgrading/downgrading plan
      if (action !== "") {
        setSelectedCardOption(cardOption);
        setShowConfirmModal(true);
      } else {
        if (cardOption === 0) {
          mixpanel.track("Webapp: Pressed Subscription Plan Option Free", {
            distinct_id: accountData.uid,
            is_onboarding: isOnboarding,
          });
          if (isOnboarding && onboardingFreeOnPress) {
            onboardingFreeOnPress();
          } else {
            navigate("/home");
          }
        } else {
          // normal subscription checkout
          mixpanel.track("Webapp: Pressed Subscription Plan Option", {
            distinct_id: accountData.uid,
            subscriptionPlan: testStripe(accountData.uid)
              ? subscriptionCard.testPriceId
              : subscriptionCard.priceId,
            subscriptionPrice: subscriptionCard.price,
            is_onboarding: isOnboarding,
          });
          setSelectedCardOption(cardOption);
          setSubscriptionStage(SubscriptionStage.CHECKOUT);
        }
      }
    },
    [
      loading,
      accountData,
      changePlan,
      mostRecentPriceAmount,
      subscriptionCard.price,
      subscriptionCard.testPriceId,
      subscriptionCard.priceId,
      mixpanel,
      isOnboarding,
      onboardingFreeOnPress,
      navigate,
    ]
  );

  return (
    <div className="ColumnNormal" style={{ paddingBlock: 24, height: "100%" }}>
      {subscriptionStage === SubscriptionStage.RECEIPT ? (
        <SubscriptionReceipt
          subscriptionPlan={subscriptionCard}
          email={email}
          taxAmount={taxAmount}
        />
      ) : subscriptionStage === SubscriptionStage.CHECKOUT ? (
        <SubscriptionCheckout
          subscriptionPlan={subscriptionCard}
          setSubscriptionStage={setSubscriptionStage}
          email={email}
          setEmail={setEmail}
          taxAmount={taxAmount}
          setTaxAmount={setTaxAmount}
        />
      ) : (
        <div className="ColumnNormal">
          <div
            className={isDesktopSize ? "ColumnCentering" : "ColumnNormal"}
            style={{
              gap: 30,
              paddingInline: 14,
              paddingTop: 40,
              paddingBottom: isDesktopSize ? 30 : 0,
            }}
          >
            <div
              className={isDesktopSize ? "ColumnCentering" : "ColumnNormal"}
              style={{ gap: 20 }}
            >
              <div>
                <img
                  style={{ height: 30, width: "auto", objectFit: "cover" }}
                  src={MarkitLogoBlue}
                  alt={"markitLogoBlue"}
                />
              </div>
              <div
                className={isDesktopSize ? "ColumnCentering" : "ColumnNormal"}
                style={{ gap: 12 }}
              >
                <span style={{ fontSize: 28, fontWeight: 500 }}>
                  Select a plan to continue
                </span>
                <span
                  className="bodySubtextMedium"
                  style={{
                    textAlign: isDesktopSize ? "center" : "left",
                  }}
                >
                  Build, monetize, and scale your audience with text.
                  <br />
                  Start free. Upgrade to Markit+ as you grow
                </span>
              </div>
              {hasOnboardingFlowFeatures ? (
                <div
                  className="Centering"
                  style={{
                    backgroundColor: Colors.BLUE5,
                    borderRadius: 12,
                    padding: "10px 14px",
                  }}
                >
                  <span
                    className="bodySmallMedium"
                    style={{ color: Colors.WHITE }}
                  >
                    Some features you selected require Markit+
                  </span>
                </div>
              ) : null}
            </div>
            {!isDesktopSize ? (
              <FlatMobileTabs
                tabValue={selectedCardOption}
                setTabValue={setSelectedCardOption}
                tabNames={[
                  PlanTypeV4.FREE.name,
                  PlanTypeV4.ESSENTIAL.name,
                  PlanTypeV4.PRO.name,
                  PlanTypeV4.BUSINESS.name,
                ]}
                spacing={24}
                containerStyles={{ paddingLeft: 10 }}
              />
            ) : null}
          </div>
          <div
            className="ColumnNormal"
            style={{
              backgroundColor: Colors.WHITE1,
              padding: isDesktopSize
                ? "30px 80px 80px 80px"
                : "24px 14px 40px 14px",
            }}
          >
            <SubscriptionPlans
              selectedCardOption={selectedCardOption}
              setSelectedCardOption={setSelectedCardOption}
              itemOnPress={subscribe}
            />
          </div>
        </div>
      )}
      <ConfirmActionModal
        heading={"Upgrade Subscription?"}
        subtext={
          "Are you sure you want to upgrade your subscription? Your card on file will be charged now, and your subscription will update once that's confirmed."
        }
        confirmButtonText="Yes"
        hideModal={!showConfirmModal}
        setIsVisible={setShowConfirmModal}
        confirmOnPress={upgradeSubscription}
        loadingConfirm={upgradeLoading}
      />
      <ConfirmActionModal
        heading={"Card On File Couldn't Be Charged"}
        subtext={
          "We weren't able to charge the card on file for this upgrade. Pay the invoice below to complete your upgrade - it'll take effect as soon as the invoice is paid."
        }
        confirmButtonText="Pay Invoice"
        hideModal={!showInvoiceModal}
        setIsVisible={setShowInvoiceModal}
        confirmOnPress={() => {
          if (pendingUpgrade?.hostedInvoiceUrl) {
            window.open(pendingUpgrade.hostedInvoiceUrl, "_blank");
          }
        }}
      />
      {alertText ? (
        <AlertContainer
          headerComp={<span>{alertText}</span>}
          closeModal={() => setAlertText("")}
          closeOnOutsidePress={true}
          hideModal={false}
        />
      ) : null}
    </div>
  );
};

export default SubscriptionPlansFlow;
